'use client';

import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Paperclip, X, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AnthropicFileInfo } from '@/types/chat';

interface ChatInputProps {
  onSend: (message: string) => void;
  onFileUpload?: (file: File) => void;
  showFileUpload?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  uploadedFiles?: AnthropicFileInfo[];
}

/**
 * Chat input with auto-expanding textarea and send button.
 *
 * Features:
 * - Auto-expands as user types (up to 5 lines)
 * - Send on Enter (Shift+Enter for new line)
 * - Loading state while waiting for response
 * - Disabled state when no session is active
 * - Optional file upload button (paperclip icon)
 * - Persistent file chips from API (uploadedFiles)
 * - Local file chip during selection/upload transition
 */
export function ChatInput({
  onSend,
  onFileUpload,
  showFileUpload = false,
  isLoading = false,
  disabled = false,
  placeholder = 'Ask a question about your cube data...',
  uploadedFiles = [],
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploading, setFileUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Clear local file once it appears in the API-backed uploadedFiles
  useEffect(() => {
    if (selectedFile && uploadedFiles.some(f => f.filename === selectedFile.name)) {
      setSelectedFile(null);
      setFileUploading(false);
    }
  }, [uploadedFiles, selectedFile]);

  const handleSubmit = () => {
    if (isLoading || disabled) return;

    // If a file is selected (not yet in API) and handler exists, upload the file
    if (selectedFile && onFileUpload && !uploadedFiles.some(f => f.filename === selectedFile.name)) {
      onFileUpload(selectedFile);
      setFileUploading(true);
      setMessage('');
      return;
    }

    const trimmed = message.trim();
    if (trimmed) {
      onSend(trimmed);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const clearFile = () => {
    setSelectedFile(null);
  };

  // Only show local chip if it's not yet tracked by the API
  const showLocalChip = selectedFile && !uploadedFiles.some(f => f.filename === selectedFile.name);
  const hasNewFile = showLocalChip && !!onFileUpload;
  const canSubmit = hasNewFile ? true : !!message.trim();

  return (
    <div className="border-t border-border-subtle bg-surface-base p-4">
      {/* Persistent file chips from API */}
      {uploadedFiles.length > 0 && (
        <div className="max-w-4xl mx-auto mb-2 flex flex-wrap gap-2">
          {uploadedFiles.map(file => (
            <div
              key={file.id}
              className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5 text-sm text-blue-700 dark:text-blue-300"
            >
              <Paperclip className="w-3.5 h-3.5" />
              <span className="truncate max-w-[200px]">{file.filename}</span>
              {file.is_committed && <CheckCircle2 className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />}
            </div>
          ))}
        </div>
      )}

      {/* Local file chip (selected but not yet uploaded to API) */}
      {showLocalChip && (
        <div className="max-w-4xl mx-auto mb-2">
          <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5 text-sm text-blue-700 dark:text-blue-300">
            <Paperclip className="w-3.5 h-3.5" />
            <span className="truncate max-w-[200px]">{selectedFile!.name}</span>
            {fileUploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <button onClick={clearFile} className="hover:text-blue-900 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-end gap-3 max-w-4xl mx-auto">
        {/* File Upload Button */}
        {showFileUpload && (
          <>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isLoading}
              variant="ghost"
              size="icon"
              className="rounded-xl h-11 w-11 flex-shrink-0 text-text-placeholder hover:text-text-secondary"
              title="Attach Excel file"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
          </>
        )}

        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            rows={1}
            className={cn(
              'w-full resize-none rounded-xl border-2 border-border-subtle px-4 py-3',
              'bg-surface-base text-text-primary text-sm placeholder:text-text-placeholder',
              'focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20',
              'disabled:bg-surface-raised disabled:text-text-placeholder disabled:cursor-not-allowed',
              'transition-all duration-200'
            )}
          />
        </div>

        {/* Send Button */}
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isLoading || disabled}
          variant="coral"
          size="icon"
          className="rounded-xl h-11 w-11 flex-shrink-0"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </Button>
      </div>

      {/* Hint text */}
      <p className="text-xs text-text-placeholder text-center mt-2">Press Enter to send, Shift+Enter for new line</p>
    </div>
  );
}
